import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { materialModules } from '../angular-material.module';
import { AddUserDialogComponent } from './components/add-user-dialog/add-user-dialog.component';
import { AssetManagerDialogComponent } from './components/asset-manager-dialog/asset-manager-dialog.component';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { ButtonSelectComponent } from './components/button-select/button-select.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { DelinquentBusinessDialogComponent } from './components/delinquent-business-dialog/delinquent-business-dialog.component';
import { SearchArrayByPipe } from './pipes/search-array-by.pipe';
import { DraggablePlayheadDirective } from './directives/draggable-playhead.directive';
import { VideoPlayerDialogComponent } from './components/video-player-dialog/video-player-dialog.component';
import { DisclaimerDialogComponent } from './components/disclaimer-dialog/disclaimer-dialog.component';
import { DragResizeDirective } from './directives/drag-resize.directive';
import { ZoomPanComponent } from './components/zoom-pan/zoom-pan.component';
import { FileUploaderComponent } from './components/file-uploader/file-uploader.component';
import { FileDropOverlayComponent } from './components/file-drop-overlay/file-drop-overlay.component';
import { AccountBarComponent } from './components/header/account-bar/account-bar.component';
import { EditorNavigationComponent } from './components/header/editor-navigation/editor-navigation.component';
import { HeaderComponent } from './components/header/header.component';
import { LogoComponent } from './components/header/logo/logo.component';
import { ProjectsNavigationComponent } from './components/header/projects-navigation/projects-navigation.component';
import { VersionSelectorComponent } from './components/header/version-selector/version-selector.component';
import { ProjectDetailsDialogComponent } from './components/project-details-dialog/project-details-dialog.component';
import { SelectDialogComponent } from './components/select-dialog/select-dialog.component';
import { VersionNumberComponent } from './components/version-number/version-number.component';
import { NewsPopupDialogComponent } from './components/news-popup-dialog/news-popup-dialog.component';
import { AssetMediaComponent } from './components/asset-media/asset-media.component';
import { DraggableSegmentDirective } from './directives/draggable-segment.directive';
import { DropFileDirective } from './directives/drop-file.directive';
import { LocalVarDirective } from './directives/local-var.directive';
import { ResizeColumnDirective } from './directives/resize-column.directive';
import { ColonToDotPipe } from './pipes/colon-to-dot.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { MarkDownPipe } from './pipes/markdown.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { ThumbnailPipe } from './pipes/thumbnail.pipe';
import { OrderByAlphaPipe } from './pipes/orderByAlpha.pipe';
import { AddExternalPaymentMethodDialogComponent } from './components/add-external-payment-method-dialog/add-external-payment-method-dialog.component';
import { DataImportDialogComponent } from './components/data-import-dialog/data-import-dialog.component';
import { DataSourceManagerDialogComponent } from './components/data-source-manager-dialog/data-source-manager-dialog.component';
import { FileSizeFormatPipe } from './pipes/file-size-format.pipe';
import { CurrencyEnvPipe } from './pipes/currency-env.pipe';
import { BusinessTrialAlertComponent } from './components/business-trial-alert/business-trial-alert.component';
import { OnboardPartsDialogComponent } from './components/onboard-parts-dialog/onboard-parts-dialog.component';
import { ObjectValuesPipe } from './pipes/object-values.pipe';
import { ClipTagsPipe } from './pipes/clip-tags.pipe';
import { ProjectPosterPipe } from './pipes/project-poster.pipe';
import { TippyDirective } from '@ngneat/helipopper';
import { ResizableColumnDirective } from './directives/resizable-column.directive';
import { MapPipe } from './pipes/map.pipe';
import { ClickProgressDirective } from './directives/click-progress.directive';
import { ScrubProgressDirective } from './directives/scrub-progress.directive';
import { FlattenLayersPipe } from './pipes/flatten-frame-layers.pipe';
import { FilterByVisibilityConditionsPipe } from './pipes/filter-by-visibility-condition.pipe';
import { ImageEditorDialogComponent } from './image-editor-dialog/image-editor-dialog.component';
import { ImageEditorSidebarComponent } from './image-editor-dialog/image-editor-sidebar/image-editor-sidebar.component';
import { ImageEditorVariationTrayComponent } from './image-editor-dialog/image-editor-variation-tray/image-editor-variation-tray.component';
import { InputModule } from './components/input/input.module';
import { ImageToolBackgroundComponent } from './image-editor-dialog/tools/image-tool-background/image-tool-background.component';
import { ImageToolImageGenerationComponent } from './image-editor-dialog/tools/image-tool-image-generation/image-tool-image-generation.component';
import { ImageToolEditWithAiComponent } from './image-editor-dialog/tools/image-tool-edit-with-ai/image-tool-edit-with-ai.component';
import { CloudinaryVideoTransformPipe } from './pipes/cloudinary-video-transform.pipe';
import { CloudinaryImageTransformPipe } from './pipes/cloudinary-image-transform.pipe';
import { ArrayFilterByPropertyExistsPipe } from './pipes/array-filter-by-propery-exists.pipe';
import { ExportDownloadComponent } from './components/export-download/export-download.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StringifyPipe } from './pipes/stringify.pipe';
import { EllipsisTooltipDirective } from './directives/ellipsis-tooltip.directive';
import { TabNavigationComponent } from './components/tab-navigation/tab-navigation.component';
import { PickFromArrayPipe } from './pipes/pick-from-array.pipe';
import { DisablePointerEventsDirective } from './directives/disable-pointer-events.directive';
import { QueryAnimationDialogComponent } from './components/query-animation-dialog/query-animation-dialog.component';
import { DimensionPipe } from './pipes/dimension.pipe';
import { RenameModalComponent } from './components/tab-navigation/rename-modal/rename-modal.component';
import { SuperAdminPipe } from './pipes/super-admin.pipe';
import { SharedDirectiveModule } from './directives/shared-directive.module';
import { OutdatedBrowserAlertComponent } from './components/outdated-browser-alert/outdated-browser-alert.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getPaginatorIntl } from '../_core/services/localization.service';
import { LanguageSelectorComponent } from './components/header/language-selector/language-selector.component';
import { SetLocaleDialogComponent } from './locale/set-locale-dialog/set-locale-dialog.component';
import { ProjectItemThumbnailCardHoverComponent } from './components/project-item-thumbnail-card-hover/project-item-thumbnail-card-hover.component';
import { AnchorSelectedTab } from './directives/anchor-selected-tab.directive';
import { ImageCropperModule } from './components/image-cropper/image-cropper.module';
import { ImageToolCropComponent } from './image-editor-dialog/tools/image-tool-crop/image-tool-crop.component';
import { ItemReviewStatusComponent } from './components/item-review-status/item-review-status.component';
import { ProjectListingsThumbnailsPipe } from './pipes/project-listings-thumbnails.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { ExceededSupportedWidthAlertComponent } from './components/exceeded-supported-width-browser-alert/exceeded-supported-width-alert.component';
import { DisableExportUnapprovedGroupPipe } from '../_core/state/export-action/asset-export/pipes/disable-export-unapproved-group.pipe';
import { DisableExportUnapprovedItemsPipe } from '../_core/state/export-action/asset-export/pipes/disable-export-unapproved-items.pipe';
import { SharedPipeModule } from './pipes/shared-pipe.module';
import { BriaIframeComponent } from './components/bria-iframe/bria-iframe.component';
import { PhotopeaIframeComponent } from './components/photopea-iframe/photopea-iframe.component';
import { LocalizeWithLocMapPipe } from './pipes/localize-with-loc-map.pipe';
import { VisibilityConditionsDialogComponent } from './components/visibility-conditions-dialog/visibility-conditions-dialog.component';
import { RecursiveConditionGroupComponent } from './components/visibility-conditions-dialog/recursive-condition-group/recursive-condition-group.component';
import { ExportDownloadDialogComponent } from './components/export-download-dialog/export-download-dialog.component';
import { MessageDialogComponent } from './components/message-dialog/message-dialog.component';
import { VideoEditorDialogComponent } from './video-editor-dialog/video-editor-dialog.component';
import { VideoEditorSidebarComponent } from './video-editor-dialog/video-editor-sidebar/video-editor-sidebar.component';
import { VideoToolCropComponent } from './video-editor-dialog/tools/video-tool-crop/video-tool-crop.component';
import { VideoToolTrimComponent } from './video-editor-dialog/tools/video-tool-trim/video-tool-trim.component';
import { VideoToolAiGenerationComponent } from './video-editor-dialog/tools/video-tool-ai-generation/video-tool-ai-generation.component';
import { ContentCropperComponent } from './components/content-cropper/content-cropper.component';
import { ExportPackageJsonDialogComponent } from './components/export-package-json-dialog/export-package-json-dialog.component';
import { DynamicJsonFormDialogComponent } from './components/dynamic-json-form-dialog/dynamic-json-form-dialog.component';
import { ContextMenuComponent } from './components/context-menu/context-menu.component';
import { ContextMenuDirective } from './components/context-menu/context-menu.directive';
import { VolumeControlMenuComponent } from './components/volume-control-menu/volume-control-menu.component';
import { ResizableRowDirective } from './directives/resizable-row.directive';
import { UserPermissionsRolesDialogComponent } from './components/user-permissions-roles-dialog/user-permissions-roles-dialog.component';
import { DraggableListComponent } from './components/draggable-list/draggable-list.component';
import { AddAuthMethodDialogComponent } from './components/add-auth-method-dialog/add-auth-method-dialog.component';
import { AutofocusDirective } from './directives/autofocus-input.directive';
import { TemplateVersionSelectorComponent } from './components/template-version-selector/template-version-selector.component';
import { PublishVersionDialogComponent } from './components/publish-version-dialog/publish-version-dialog.component';
import { HasChangesPipe } from './pipes/has-changes.pipe';
import { ItemReviewStatusSumComponent } from './components/item-review-status/item-review-status-sum/item-review-status-sum.component';
import { ExportDownloadGroupRowComponent } from './components/export-download/export-download-group-row/export-download-group-row.component';
import { ExportDownloadItemRowComponent } from './components/export-download/export-download-item-row/export-download-item-row.component';
import { ExportDownloadSubgroupRowComponent } from './components/export-download/export-download-subgroup-row/export-download-subgroup-row.component';
import { ThemeSelectorComponent } from "./components/header/theme-selector/theme-selector.component";
import { LayerActionsOptionsComponent } from './components/layer-actions-options/layer-actions-options.component';
import { LayerActionEditorDialogComponent } from './components/layer-action-editor-dialog/layer-action-editor-dialog.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { FilenameTemplateComponent } from './components/filename-template/filename-template.component';
import { ExportCropBoxComponent } from './components/export-crop-box/export-crop-box.component';
import { ExportDownloadErrorStatusComponent } from './components/export-download/export-download-error-status/export-download-error-status.component';
import { AdImagePreviewDialogComponent } from '../_core/state/ad/ad-preview/ad-image-preview-dialog/ad-image-preview-dialog.component';
import { FocalAreaCroppingComponent } from './image-editor-dialog/focal-area-cropping/focal-area-cropping.component';
import { FocalAreaSidebarComponent } from './image-editor-dialog/focal-area-cropping/focal-area-sidebar/focal-area-sidebar.component';
import { FocalAreaCardComponent } from './image-editor-dialog/focal-area-cropping/focal-area-card/focal-area-card.component';
import { FocalAreaPreviewComponent } from './image-editor-dialog/focal-area-cropping/focal-area-preview/focal-area-preview.component';
import { AiSuggestionsComponent } from './image-editor-dialog/focal-area-cropping/ai-suggestions/ai-suggestions.component';
import { FocalAreaDetectionInfoComponent } from './image-editor-dialog/focal-area-cropping/focal-area-detection-info/focal-area-detection-info.component';
import { CompareByPropertiesPipe } from './pipes/compare-by-properties.pipe';
import {
	BusinessSelectionInputComponent
} from "./components/input/business-selection-input/business-selection-input.component";
import { AiModule } from './ai/ai.module';
import { SidebarAssetsGridComponent } from './components/sidebar-assets-grid/sidebar-assets-grid.component';
import { AssetManagerCardComponent } from './components/asset-manager-dialog/asset-manager-card/asset-manager-card.component';
import { ViewModeToggleComponent } from './components/view-mode-toggle/view-mode-toggle.component';

@NgModule({
	imports: [
		CommonModule,
		RouterModule,
		FormsModule,
		InputModule,
		TippyDirective,
		ImageCropperModule,
		ReactiveFormsModule,
		...materialModules,
		MatProgressBarModule,
		SharedDirectiveModule,
		SharedPipeModule,
		ItemReviewStatusComponent,
		ProjectListingsThumbnailsPipe,
		DragDropModule,
		ItemReviewStatusSumComponent,
		ThemeSelectorComponent,
		AiSuggestionsComponent,
		BusinessSelectionInputComponent,
		AiModule
	],
	declarations: [
		VersionNumberComponent,
		ColonToDotPipe,
		HeaderComponent,
		LanguageSelectorComponent,
		LogoComponent,
		AccountBarComponent,
		EditorNavigationComponent,
		ProjectsNavigationComponent,
		DurationPipe,
		ThumbnailPipe,
		VersionSelectorComponent,
		DraggableSegmentDirective,
		DraggablePlayheadDirective,
		DropFileDirective,
		SelectDialogComponent,
		ProjectDetailsDialogComponent,
		UserPermissionsRolesDialogComponent,
		AddUserDialogComponent,
		AssetManagerDialogComponent,
		FileUploaderComponent,
		FileDropOverlayComponent,
		LocalVarDirective,
		ConfirmDialogComponent,
		MessageDialogComponent,
		DelinquentBusinessDialogComponent,
		VideoPlayerDialogComponent,
		SafeHtmlPipe,
		OrderByAlphaPipe,
		MarkDownPipe,
		AutocompleteComponent,
		ButtonSelectComponent,
		NewsPopupDialogComponent,
		AssetMediaComponent,
		OnboardPartsDialogComponent,
		ResizeColumnDirective,
		SearchArrayByPipe,
		DisclaimerDialogComponent,
		DragResizeDirective,
		ZoomPanComponent,
		AddExternalPaymentMethodDialogComponent,
		DataImportDialogComponent,
		FileSizeFormatPipe,
		ProjectPosterPipe,
		ResizableColumnDirective,
		ResizableRowDirective,
		CurrencyEnvPipe,
		BusinessTrialAlertComponent,
		ObjectValuesPipe,
		ClipTagsPipe,
		MapPipe,
		ClickProgressDirective,
		ScrubProgressDirective,
		FlattenLayersPipe,
		FilterByVisibilityConditionsPipe,
		ImageEditorDialogComponent,
		ImageEditorSidebarComponent,
		ImageEditorVariationTrayComponent,
		ImageToolBackgroundComponent,
		ImageToolImageGenerationComponent,
		ImageToolEditWithAiComponent,
		ImageToolCropComponent,
		VideoEditorDialogComponent,
		VideoEditorSidebarComponent,
		VideoToolCropComponent,
		VideoToolTrimComponent,
		VideoToolAiGenerationComponent,
		FilterByVisibilityConditionsPipe,
		CloudinaryVideoTransformPipe,
		CloudinaryImageTransformPipe,
		ArrayFilterByPropertyExistsPipe,
		ExportDownloadComponent,
		ExportDownloadErrorStatusComponent,
		ExportDownloadGroupRowComponent,
		ExportDownloadSubgroupRowComponent,
		ExportDownloadItemRowComponent,
		ExportDownloadDialogComponent,
		StringifyPipe,
		TabNavigationComponent,
		PickFromArrayPipe,
		DisablePointerEventsDirective,
		QueryAnimationDialogComponent,
		DimensionPipe,
		RenameModalComponent,
		SuperAdminPipe,
		DisableExportUnapprovedGroupPipe,
		DisableExportUnapprovedItemsPipe,
		OutdatedBrowserAlertComponent,
		SetLocaleDialogComponent,
		LocalizedDatePipe,
		ProjectItemThumbnailCardHoverComponent,
		AnchorSelectedTab,
		ExceededSupportedWidthAlertComponent,
		BriaIframeComponent,
		PhotopeaIframeComponent,
		LocalizeWithLocMapPipe,
		VisibilityConditionsDialogComponent,
		RecursiveConditionGroupComponent,
		ContentCropperComponent,
		ExportPackageJsonDialogComponent,
		DynamicJsonFormDialogComponent,
		ContextMenuComponent,
		ContextMenuDirective,
		VolumeControlMenuComponent,
		DraggableListComponent,
		TemplateVersionSelectorComponent,
		AddAuthMethodDialogComponent,
		AutofocusDirective,
		PublishVersionDialogComponent,
		HasChangesPipe,
		LayerActionsOptionsComponent,
		LayerActionEditorDialogComponent,
		AdImagePreviewDialogComponent,
		PaginationComponent,
		FilenameTemplateComponent,
		ExportCropBoxComponent,
		AddAuthMethodDialogComponent,
		AutofocusDirective,
		PublishVersionDialogComponent,
		HasChangesPipe,
		DataSourceManagerDialogComponent,
		FocalAreaCroppingComponent,
		FocalAreaSidebarComponent,
		FocalAreaCardComponent,
		FocalAreaPreviewComponent,
		FocalAreaDetectionInfoComponent,
		CompareByPropertiesPipe,
		DataSourceManagerDialogComponent,
		SidebarAssetsGridComponent,
		AssetManagerCardComponent,
		ViewModeToggleComponent
	],
	providers: [
		DatePipe,
		CurrencyEnvPipe,
		ProjectPosterPipe,
		ObjectValuesPipe,
		DisableExportUnapprovedGroupPipe,
		LocalizeWithLocMapPipe,
		{ provide: MatPaginatorIntl, useFactory: getPaginatorIntl }
	],
	exports: [
		InputModule,
		CommonModule,
		TippyDirective,
		VersionNumberComponent,
		ColonToDotPipe,
		HeaderComponent,
		LogoComponent,
		AccountBarComponent,
		EditorNavigationComponent,
		ProjectsNavigationComponent,
		DurationPipe,
		ThumbnailPipe,
		DraggableSegmentDirective,
		DraggablePlayheadDirective,
		LocalVarDirective,
		DropFileDirective,
		AssetManagerDialogComponent,
		VersionSelectorComponent,
		FileUploaderComponent,
		FileDropOverlayComponent,
		VideoPlayerDialogComponent,
		SafeHtmlPipe,
		OrderByAlphaPipe,
		MarkDownPipe,
		AutocompleteComponent,
		ButtonSelectComponent,
		NewsPopupDialogComponent,
		AssetMediaComponent,
		OnboardPartsDialogComponent,
		ResizeColumnDirective,
		SearchArrayByPipe,
		DisclaimerDialogComponent,
		ZoomPanComponent,
		DataImportDialogComponent,
		FileSizeFormatPipe,
		ProjectPosterPipe,
		CurrencyEnvPipe,
		BusinessTrialAlertComponent,
		ObjectValuesPipe,
		ClipTagsPipe,
		ResizableColumnDirective,
		ResizableRowDirective,
		MapPipe,
		ClickProgressDirective,
		ScrubProgressDirective,
		FlattenLayersPipe,
		FilterByVisibilityConditionsPipe,
		ImageEditorDialogComponent,
		VideoEditorDialogComponent,
		VideoEditorSidebarComponent,
		VideoToolCropComponent,
		VideoToolTrimComponent,
		VideoToolAiGenerationComponent,
		FilterByVisibilityConditionsPipe,
		CloudinaryVideoTransformPipe,
		CloudinaryImageTransformPipe,
		ArrayFilterByPropertyExistsPipe,
		ExportDownloadComponent,
		ExportDownloadErrorStatusComponent,
		ExportDownloadGroupRowComponent,
		ExportDownloadSubgroupRowComponent,
		ExportDownloadItemRowComponent,
		ExportDownloadDialogComponent,
		StringifyPipe,
		TabNavigationComponent,
		PickFromArrayPipe,
		QueryAnimationDialogComponent,
		DimensionPipe,
		SuperAdminPipe,
		EllipsisTooltipDirective,
		LanguageSelectorComponent,
		SetLocaleDialogComponent,
		AnchorSelectedTab,
		ImageToolCropComponent,
		LocalizedDatePipe,
		AnchorSelectedTab,
		ExceededSupportedWidthAlertComponent,
		OutdatedBrowserAlertComponent,
		DisableExportUnapprovedGroupPipe,
		DisableExportUnapprovedItemsPipe,
		LocalizeWithLocMapPipe,
		VisibilityConditionsDialogComponent,
		RecursiveConditionGroupComponent,
		ContentCropperComponent,
		ExportPackageJsonDialogComponent,
		DynamicJsonFormDialogComponent,
		ContextMenuDirective,
		VolumeControlMenuComponent,
		UserPermissionsRolesDialogComponent,
		DraggableListComponent,
		TemplateVersionSelectorComponent,
		AddAuthMethodDialogComponent,
		AutofocusDirective,
		PublishVersionDialogComponent,
		HasChangesPipe,
		LayerActionsOptionsComponent,
		LayerActionEditorDialogComponent,
		ExportCropBoxComponent,
		AdImagePreviewDialogComponent,
		ItemReviewStatusSumComponent,
		PaginationComponent,
		FilenameTemplateComponent,
		// Export material modules for use in other modules
		...materialModules,
		AddAuthMethodDialogComponent,
		AutofocusDirective,
		DataSourceManagerDialogComponent,
		FocalAreaCroppingComponent,
		FocalAreaSidebarComponent,
		FocalAreaCardComponent,
		FocalAreaPreviewComponent,
		FocalAreaDetectionInfoComponent,
		CompareByPropertiesPipe,
		DataSourceManagerDialogComponent,
		SidebarAssetsGridComponent,
		ViewModeToggleComponent
	]
})
export class SharedModule {}
